import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getAdminUsers, createAdminUser, toggleUserStatus, getAllOutlets, updateOutlet, toggleOutletStatus, getUtilities } from '../api';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: '', password: '', role: 'user' });
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [msg, setMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'outlets'
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  useEffect(() => {
    if (!user) return navigate('/');
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, outletsRes, utilitiesRes] = await Promise.all([
        getAdminUsers(),
        getAllOutlets(),
        getUtilities()
      ]);
      setUsers(usersRes.data);
      setOutlets(outletsRes.data);
      setUtilities(utilitiesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      const res = await createAdminUser(form);
      setMsg({ type: 'success', text: `Created ${res.data.username}` });
      setForm({ username: '', password: '', role: 'user' });
      fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.message || 'Failed' });
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await toggleUserStatus(userId, !currentStatus);
      setMsg({ type: 'success', text: 'User status updated' });
      fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to update user status' });
    }
  };

  const handleToggleOutletStatus = async (outletId, currentStatus) => {
    try {
      await toggleOutletStatus(outletId, !currentStatus);
      setMsg({ type: 'success', text: 'Outlet status updated' });
      fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to update outlet status' });
    }
  };

  const handleEditOutlet = (outlet) => {
    setEditingOutlet({ ...outlet });
  };

  const handleSaveOutlet = async () => {
    try {
      await updateOutlet(editingOutlet.id, {
        tenantName: editingOutlet.tenantName,
        location: editingOutlet.location,
        meterNo: editingOutlet.meterNo,
        serialNo: editingOutlet.serialNo,
        utilityId: editingOutlet.utilityId
      });
      setMsg({ type: 'success', text: 'Outlet updated successfully' });
      setEditingOutlet(null);
      fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to update outlet' });
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <h2>Admin — Management</h2>
        <button onClick={() => navigate('/admin')} className="btn">Back to Dashboard</button>
      </div>

      {msg && (
        <div style={{ 
          marginTop: 12, 
          padding: 12, 
          borderRadius: 6,
          background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: msg.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 18, borderBottom: '2px solid #e5e7eb' }}>
        <button 
          onClick={() => setActiveTab('users')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'users' ? '#4f46e5' : 'transparent',
            color: activeTab === 'users' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'users' ? '2px solid #4f46e5' : 'none',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Users
        </button>
        <button 
          onClick={() => setActiveTab('outlets')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'outlets' ? '#4f46e5' : 'transparent',
            color: activeTab === 'outlets' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'outlets' ? '2px solid #4f46e5' : 'none',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Outlets
        </button>
      </div>

      {loading ? (
        <div style={{ marginTop: 20 }}>Loading...</div>
      ) : (
        <>
          {activeTab === 'users' && (
            <div style={{ display: 'flex', gap: 20, marginTop: 18 }}>
              <div style={{ flex: 1 }}>
                <h3>All Users</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      {canEdit && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.username}</td>
                        <td><span className="badge badge-primary">{u.role}</span></td>
                        <td>
                          <span className="badge" style={{ 
                            background: u.active ? '#10b981' : '#ef4444',
                            color: 'white'
                          }}>
                            {u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(u.createdAt).toLocaleString()}</td>
                        {canEdit && (
                          <td>
                            <button
                              onClick={() => handleToggleUserStatus(u.id, u.active)}
                              className="btn"
                              style={{ 
                                padding: '6px 12px',
                                background: u.active ? '#ef4444' : '#10b981',
                                color: 'white'
                              }}
                            >
                              {u.active ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {canEdit && (
                <div style={{ width: 360 }}>
                  <h3>Create User</h3>
                  <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input name="username" placeholder="username" value={form.username} onChange={onChange} required />
                    <input name="password" type="password" placeholder="password" value={form.password} onChange={onChange} required />
                    <select name="role" value={form.role} onChange={onChange}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="read_admin">Read Admin</option>
                    </select>
                    <button className="btn btn-primary" type="submit">Create</button>
                  </form>
                </div>
              )}
            </div>
          )}

          {activeTab === 'outlets' && (
            <div style={{ marginTop: 18 }}>
              <h3>Outlets Management</h3>
              {canEdit && editingOutlet && (
                <div style={{ 
                  padding: 16, 
                  background: '#f9fafb', 
                  borderRadius: 8, 
                  marginBottom: 16,
                  border: '2px solid #4f46e5'
                }}>
                  <h4>Editing Outlet</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>Tenant Name</label>
                      <input 
                        value={editingOutlet.tenantName}
                        onChange={(e) => setEditingOutlet({...editingOutlet, tenantName: e.target.value})}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>Location</label>
                      <input 
                        value={editingOutlet.location}
                        onChange={(e) => setEditingOutlet({...editingOutlet, location: e.target.value})}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>Meter No</label>
                      <input 
                        value={editingOutlet.meterNo || ''}
                        onChange={(e) => setEditingOutlet({...editingOutlet, meterNo: e.target.value})}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>Serial No</label>
                      <input 
                        value={editingOutlet.serialNo}
                        onChange={(e) => setEditingOutlet({...editingOutlet, serialNo: e.target.value})}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={handleSaveOutlet} className="btn btn-primary">Save Changes</button>
                    <button onClick={() => setEditingOutlet(null)} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              )}
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Utility</th>
                    <th>Tenant</th>
                    <th>Location</th>
                    <th>Meter No</th>
                    <th>Serial No</th>
                    <th>Status</th>
                    {canEdit && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {outlets.map(outlet => (
                    <tr key={outlet.id}>
                      <td>{outlet.id}</td>
                      <td><span className="badge badge-primary">{outlet.Utility?.name}</span></td>
                      <td><strong>{outlet.tenantName}</strong></td>
                      <td>{outlet.location}</td>
                      <td>{outlet.meterNo || '-'}</td>
                      <td><span className="badge badge-success">{outlet.serialNo}</span></td>
                      <td>
                        <span className="badge" style={{ 
                          background: outlet.active ? '#10b981' : '#ef4444',
                          color: 'white'
                        }}>
                          {outlet.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canEdit && (
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => handleEditOutlet(outlet)}
                              className="btn btn-primary"
                              style={{ padding: '6px 12px', fontSize: '13px' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleOutletStatus(outlet.id, outlet.active)}
                              className="btn"
                              style={{ 
                                padding: '6px 12px',
                                fontSize: '13px',
                                background: outlet.active ? '#ef4444' : '#10b981',
                                color: 'white'
                              }}
                            >
                              {outlet.active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminUsers;
